import { useMemo, useRef, useState, useCallback } from "react";
import { FileText, Upload, Loader2, ArrowLeft, Expand, FilesIcon, CameraIcon, ChevronRight, User, Clock, Camera } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { DashboardCard } from "@/modern/components/dashboard-card";
import { CardTitle } from "@/modern/components/ui/card";
import { Badge } from "@/modern/components/ui/badge";
import { Button } from "@/modern/components/ui/button-shadcn";
import { ButtonGroup } from "@/modern/components/ui/button-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { MoreMenu, type MenuItem } from "@/modern/components/more-menu";
import FilePreview from "@/modern/components/file-preview";
import { useEntityFileContext, type FormattedFile } from "@/react/components/entityViews/files/context/EntityFileContext";
import { FileDetailModal } from "@/react/components/entityViews/files/components/editors/FileDetailModal/FileDetailModal";
import { CompanyCamProjectEditor } from "@/react/components/entityViews/files/components/editors/CompanyCamProjectEditor";
import { CompanyCamButton } from "@/react/components/entityViews/files/components/CompanyCamButton";
import { isMediaFile } from "@/other/utils/utils";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { cn } from "@/modern/utils";
import InfiniteScroll from "@/modern/components/infinite-scroll";

const transition = { duration: 0.15, ease: "easeInOut" as const };

const EXT_COLORS: Record<string, string> = {
  PDF: "bg-rose-500/10 text-rose-400",
  DOC: "bg-sky-500/10 text-sky-400",
  DOCX: "bg-sky-500/10 text-sky-400",
  XLS: "bg-emerald-500/10 text-emerald-400",
  XLSX: "bg-emerald-500/10 text-emerald-400",
  PPT: "bg-amber-500/10 text-amber-400",
  PPTX: "bg-amber-500/10 text-amber-400",
  TXT: "bg-zinc-500/10 text-zinc-400",
  CSV: "bg-teal-500/10 text-teal-400",
};

const getFileExtension = (name: string): string => {
  const parts = name.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "";
};

const PREVIEWABLE_EXTENSIONS = new Set([
  "PDF", "DOCX", "JPG", "JPEG", "PNG", "GIF", "BMP", "SVG", "WEBP", "ICO",
  "MP4", "AVI", "MOV", "WMV", "FLV", "MKV", "WEBM", "M4V",
  "MP3", "WAV", "OGG", "M4A", "FLAC", "AAC", "WMA",
]);

const hasInlinePreview = (file: FormattedFile): boolean => {
  if (file.isCCImage) return true;
  const ext = getFileExtension(file.name);
  return PREVIEWABLE_EXTENSIONS.has(ext);
};

const formatDate = (date?: Date): string => {
  if (!date) return "";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const DocumentRow = ({
  file,
  onSelect,
  onOpenEditor,
}: {
  file: FormattedFile;
  onSelect: () => void;
  onOpenEditor: () => void;
}) => {
  const ext = getFileExtension(file.name);
  const colorClass = EXT_COLORS[ext] || "bg-zinc-500/10 text-zinc-400";
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canPreview = hasInlinePreview(file);

  const handleClick = () => {
    if (!canPreview) {
      onOpenEditor();
      return;
    }
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
      onOpenEditor();
    } else {
      clickTimer.current = setTimeout(() => {
        clickTimer.current = null;
        onSelect();
      }, 250);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group flex w-full items-start gap-3 px-4 py-3 hover:bg-accent/50 transition-colors text-left rounded-sm"
    >
      <div className="relative flex size-9 shrink-0 items-center justify-center rounded-md overflow-hidden bg-muted [&_*]:!text-[0px]">
        <FilePreview
          file={file}
          src={file.thumbnailUrl}
          className="size-9 rounded-md object-cover"
          showName={false}
          fileIconClassName="size-4"
          fileIconContainerClassName="size-9"
          openFileOnClick={false}
          isThumbnail={true}
          disableLoadingState
        />
      </div>
      <div className="flex-1 min-w-0">
        {file.record?.isPhantom ? (
          <p className="text-sm text-muted-foreground flex items-center gap-1.5">
            <Loader2 className="size-3.5 shrink-0 animate-spin" />
            <span className="truncate">Uploading...</span>
          </p>
        ) : (
          <>
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <p className="truncate text-sm font-medium text-foreground/95 min-w-0">{file.name}</p>
                {ext && (
                  <span className={cn("shrink-0 inline-flex items-center justify-center rounded-md px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider leading-none", colorClass)}>
                    {ext}
                  </span>
                )}
              </div>
              <ChevronRight className="size-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
            </div>
            <div className="flex items-center gap-3 mt-1.5 overflow-hidden min-w-0">
              {file.addedBy && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
                      <User className="size-3 shrink-0" />
                      <span className="truncate">{file.addedBy}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Added by {file.addedBy}</TooltipContent>
                </Tooltip>
              )}
              {file.createdAt && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                      <Clock className="size-3 shrink-0" />
                      <span className="whitespace-nowrap">{formatDate(file.createdAt)}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{formatDate(file.createdAt)}</TooltipContent>
                </Tooltip>
              )}
              {file.isCCImage && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0">
                  <Camera className="size-3 shrink-0" />
                  <span className="whitespace-nowrap">CompanyCam</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </button>
  );
};

export const EntityDocumentView = () => {
  const {
    boardFiles,
    companyCamDocuments,
    entityRecord,
    handleFileOpen,
    openedFile,
    isFileEditorOpen,
    setIsFileEditorOpen,
    uploadNewFile,
    isFetchingDocuments,
    hasMoreDocuments,
    fetchCompanyCamDocuments,
  } = useEntityFileContext();

  const { editorHandler } = useSwivuBryntumContext();
  const uploadRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<FormattedFile | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const isInlineView = !!selectedFile;

  const documentFiles = useMemo(
    () => [...boardFiles.filter((f) => !f.record?.isPhantom && !isMediaFile(f)), ...companyCamDocuments]
      .sort((a, b) => (b.createdAt?.getTime() || 0) - (a.createdAt?.getTime() || 0)),
    [boardFiles, companyCamDocuments]
  );

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setIsUploading(true);
      await uploadNewFile(e.target.files);
      e.target.value = "";
      setIsUploading(false);
    }
  };

  const handleExpandToEditor = useCallback(() => {
    if (selectedFile) {
      handleFileOpen(selectedFile);
    }
  }, [selectedFile, handleFileOpen]);

  const handleViewAllFiles = useCallback(() => {
    editorHandler.openEditor(EDITOR_KEYS.taskFilesEditor, entityRecord, { task: entityRecord, initialTab: "documents" });
  }, [editorHandler, entityRecord]);

  const menuItems: MenuItem[] = [
    {
      itemVariant: "button" as const,
      menuText: "View All Documents",
      dropdownMenuItemProps: { onClick: handleViewAllFiles },
      Component: () => (
        <button className="flex items-center gap-2 w-full cursor-default">
          <FilesIcon className="size-4 mr-1" />
          View All Documents
        </button>
      ),
    },
    {
      itemVariant: "modal" as const,
      variant: "title" as const,
      menuText: "Link Company Cam",
      Icon: CameraIcon,
      Component: CompanyCamProjectEditor,
      modalTitle: "Link company cam project",
      menuItemModalHeaderVariant: "title" as const,
    },
  ];

  return (
    <>
      <DashboardCard
        cardContentClassName={cn("h-full flex flex-col p-0", isInlineView && "!overflow-hidden")}
        header={
          <div className="flex items-center justify-between w-full gap-2">
            <CardTitle className={cn("text-base gap-3 flex items-center truncate min-w-0")}>
              {isInlineView ? (
                <>
                  <Button variant="ghost" size="icon" className="size-7 shrink-0 p-0 text-muted-foreground" onClick={() => setSelectedFile(null)}>
                    <ArrowLeft className="size-4" />
                  </Button>
                  <span className="truncate min-w-0 pr-2">{selectedFile.name}</span>
                </>
              ) : (
                <>
                  <span className="truncate">Documents</span>
                  {documentFiles.length > 0 && (
                    <Badge variant="muted" size="xs" className="shrink-0">{documentFiles.length}</Badge>
                  )}
                  {isUploading && <span className="flex items-center gap-1.5 text-xs text-muted-foreground shrink-0"><Loader2 className="size-3 animate-spin" />Uploading...</span>}
                </>
              )}
            </CardTitle>

            <div className="flex items-center gap-2 shrink-0">
              {isInlineView ? (
                <ButtonGroup>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="entityHeader" className="w-auto px-3" size="icon" onClick={handleExpandToEditor}>
                        <Expand className="size-4" /> Edit
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Open in editor</TooltipContent>
                  </Tooltip>
                </ButtonGroup>
              ) : (
                <>
                  <CompanyCamButton />
                  <ButtonGroup>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="entityHeader" size="icon" onClick={() => uploadRef.current?.click()}>
                          <Upload className="size-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Upload document</TooltipContent>
                    </Tooltip>
                    <MoreMenu menuItems={menuItems} record={entityRecord} menuTitle="Documents" triggerButtonVariant="entityHeader" />
                  </ButtonGroup>
                  <input ref={uploadRef} type="file" className="hidden" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv" onChange={handleUpload} />
                </>
              )}
            </div>
          </div>
        }
      >
        <AnimatePresence mode="wait">
          {documentFiles.length === 0 && !isInlineView ? (
            <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={transition} className="flex flex-1 items-center justify-center h-full p-4">
              <div className="h-full w-full">
                <div className="min-h-[12rem] h-full flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-muted-foreground/30 bg-muted/40 p-4 text-center shadow-sm">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <FileText className="size-5" />
                  </span>
                  <div className="space-y-1">
                    <h3 className="text-sm font-semibold text-foreground">No documents yet</h3>
                    <p className="text-xs text-muted-foreground">Upload documents to see them here.</p>
                  </div>
                  <Button size="sm" onClick={() => uploadRef.current?.click()}>
                    <Upload className="size-4 mr-1" />
                    Upload
                  </Button>
                </div>
              </div>
            </motion.div>
          ) : isInlineView ? (
            <motion.div
              key="preview"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0 }}
              transition={transition}
              className="relative h-full flex items-center justify-center p-1 overflow-auto"
            >
              <FilePreview
                file={selectedFile}
                src={selectedFile.originalImageUrl || selectedFile.thumbnailUrl}
                className="max-w-full max-h-full object-contain rounded-md"
                showName={false}
                openFileOnClick={false}
                disableLoadingState
              />
            </motion.div>
          ) : (
            <motion.div
              key="list"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, x: -20 }}
              transition={transition}
              className="flex-1 overflow-y-auto overflow-x-hidden"
            >
              <div className="[&>*+*]:border-t [&>*+*]:border-border/50">
                {documentFiles.map((file) => (
                  <DocumentRow
                    key={file.id}
                    file={file}
                    onSelect={() => setSelectedFile(file)}
                    onOpenEditor={() => handleFileOpen(file)}
                  />
                ))}
              </div>
              {(hasMoreDocuments || isFetchingDocuments) && (
                <InfiniteScroll
                  isLoading={isFetchingDocuments}
                  hasMore={hasMoreDocuments}
                  next={fetchCompanyCamDocuments}
                  rootMargin="50px"
                  threshold={0}
                >
                  <div className="flex items-center justify-center py-4">
                    {isFetchingDocuments && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                  </div>
                </InfiniteScroll>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </DashboardCard>

      {openedFile && (
        <FileDetailModal
          entityRecord={entityRecord}
          file={openedFile}
          open={isFileEditorOpen}
          setOpen={setIsFileEditorOpen}
        />
      )}
    </>
  );
};
